/**
 * "Enroll in sequence" for a lead selection (Lead Database) - enrolls the
 * selected leads in an approved sequence. The backend skips suppressed,
 * archived, won/lost, contact-less, out-of-scope and already-enrolled leads
 * and reports why; nothing is sent from here (the scheduled sender does
 * that, inside the business-hours window and under the daily cap).
 */
import React, { useEffect, useState } from 'react';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { toast } from 'sonner';
import { campaignFetch, SKIP_REASONS } from '@/campaigns/campaignApi';

const ENROLLABLE = ['approved', 'active', 'paused'];

const EnrollInSequenceDialog = ({ open, onClose, token, leadIds, onDone }) => {
  const [sequences, setSequences] = useState([]);
  const [sequenceId, setSequenceId] = useState('');
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);

  useEffect(() => {
    if (!open) return;
    setResult(null);
    setSequenceId('');
    campaignFetch(token, '/campaigns/sequences')
      .then((data) => setSequences((data.sequences || []).filter((s) => ENROLLABLE.includes(s.status))))
      .catch((error) => toast.error(error.message));
  }, [open, token]);

  const enroll = async () => {
    setBusy(true);
    try {
      const data = await campaignFetch(token, `/campaigns/sequences/${sequenceId}/enroll`, { method: 'POST', body: { lead_ids: leadIds } });
      setResult(data);
      if (data.enrolled_count) toast.success(`${data.enrolled_count} lead(s) enrolled`);
      if (onDone) onDone(data);
    } catch (error) {
      toast.error(error.message);
    } finally {
      setBusy(false);
    }
  };

  const skippedByReason = (result?.skipped || []).reduce((acc, s) => ({ ...acc, [s.reason]: (acc[s.reason] || 0) + 1 }), {});

  return (
    <Dialog open={open} onOpenChange={(next) => { if (!next) onClose(); }}>
      <DialogContent data-testid="enroll-sequence-dialog">
        <DialogHeader>
          <DialogTitle>Enroll {leadIds.length} lead{leadIds.length === 1 ? '' : 's'} in a sequence</DialogTitle>
          <DialogDescription>Only approved sequences are listed. Emails go out later, inside the business-hours window and under the daily cap.</DialogDescription>
        </DialogHeader>
        {!result && (
          <div className="space-y-3">
            <select
              value={sequenceId}
              onChange={(e) => setSequenceId(e.target.value)}
              className="w-full bg-white/5 border border-white/10 text-white rounded-md px-3 py-2"
              data-testid="enroll-sequence-select"
            >
              <option value="">Choose a sequence...</option>
              {sequences.map((s) => <option key={s.id} value={s.id}>{s.name} ({s.status})</option>)}
            </select>
            {sequences.length === 0 && <p className="text-sm text-white/60">No approved sequences yet - build and approve one in Cold Email.</p>}
            <Button className="primary-btn" disabled={!sequenceId || busy || leadIds.length === 0} onClick={enroll} data-testid="enroll-sequence-confirm">
              {busy ? 'Enrolling...' : 'Enroll'}
            </Button>
          </div>
        )}
        {result && (
          <div className="space-y-2" data-testid="enroll-sequence-result">
            <p><strong>{result.enrolled_count}</strong> enrolled, <strong>{result.skipped_count}</strong> skipped.</p>
            {Object.entries(skippedByReason).map(([reason, count]) => (
              <p key={reason} className="text-sm text-white/70">{count} × {SKIP_REASONS[reason] || reason}</p>
            ))}
            <Button className="ghost-btn" onClick={onClose}>Close</Button>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default EnrollInSequenceDialog;
